import React from "react";
import { Link } from "react-router-dom";
import { ArrowRight, ArrowUpRight, Check, Download, FileText, Droplets, Factory, Thermometer, Boxes, Sparkles } from "lucide-react";
import { motion } from "framer-motion";
import { useLang } from "../i18n/LanguageContext";
import { Seo } from "../components/Seo";
import { Breadcrumbs } from "../components/Breadcrumbs";
import { Reveal, Stagger, itemVariant } from "../components/Reveal";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "../components/ui/accordion";
import { NORDFLEX_TPU as P } from "../data/products";
import { COMPANY } from "../data/content";

const DOMAIN = "https://www.plasto-nord.com";

function ParamTable({ table, testid }) {
  return (
    <div className="overflow-x-auto rounded-xl border border-[#e4e9f0]" data-testid={testid}>
      <table className="w-full border-collapse text-left text-sm">
        <thead>
          <tr className="bg-[#16181D] text-white">
            {table.head.map((h, i) => <th key={i} className="px-4 py-3 font-head font-semibold">{h}</th>)}
          </tr>
        </thead>
        <tbody>
          {table.rows.map((row, ri) => (
            <tr key={ri} className={ri % 2 ? "bg-[#f6f8fb]" : "bg-white"}>
              {row.map((cell, ci) => (
                <td key={ci} className={`px-4 py-3 align-top text-[#4a4d52] ${ci === 0 ? "font-medium text-[#16181D]" : ""}`}>{cell}</td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function Block({ title, icon: Icon, children, id }) {
  return (
    <section className="py-12 md:py-16" id={id}>
      <div className="pn-container">
        <Reveal>
          <div className="flex items-center gap-3">
            {Icon && <span className="grid h-10 w-10 place-items-center rounded-lg bg-[#FBEADD] text-[#E2611C]"><Icon size={20} /></span>}
            <h2 className="font-head text-2xl font-semibold tracking-tight text-[#16181D] md:text-3xl">{title}</h2>
          </div>
        </Reveal>
        <div className="mt-7">{children}</div>
      </div>
    </section>
  );
}

export default function NordflexTpu() {
  const { lang, pick } = useLang();
  const L = (v) => (v && typeof v === "object" && !Array.isArray(v) ? pick(v) : v);
  const T = (de, en) => (lang === "en" ? en : de);
  const localTable = (tbl) => ({
    head: lang === "en" && tbl.headEn ? tbl.headEn : tbl.head,
    rows: tbl.rows.map((r) => r.map((c) => L(c))),
  });
  const crumbs = [{ name: "Home", path: "/" }, { name: T("Produkte", "Products"), path: "/produkte" }, { name: P.name, path: `/produkte/${P.slug}` }];

  const productSchema = {
    "@context": "https://schema.org", "@type": "Product", name: P.name, description: L(P.metaDescription),
    image: P.heroImage, category: L(P.material), brand: { "@type": "Brand", name: "NORDFLEX®" },
    manufacturer: { "@id": DOMAIN + "/#organization" }, material: "Thermoplastic Polyurethane (TPU)",
    additionalProperty: P.types.rows.map((r) => ({ "@type": "PropertyValue", name: r[0], value: r[1] })),
  };
  const faqSchema = { "@context": "https://schema.org", "@type": "FAQPage",
    mainEntity: P.faq.map((f) => ({ "@type": "Question", name: L(f.q), acceptedAnswer: { "@type": "Answer", text: L(f.a) } })) };

  return (
    <div data-testid="product-nordflex-tpu">
      <Seo title={L(P.metaTitle)} description={L(P.metaDescription)} path={`/produkte/${P.slug}`} image={P.heroImage} breadcrumbs={crumbs} schema={[productSchema, faqSchema]} />

      {/* HERO */}
      <section className="border-b border-[#e4e9f0] bg-[#f6f8fb] grain">
        <div className="pn-container grid items-center gap-10 py-12 md:py-16 lg:grid-cols-[1.05fr_0.95fr]">
          <div>
            <Breadcrumbs items={crumbs} />
            <Reveal>
              <p className="overline">NORDFLEX® · {L(P.material)}</p>
              <h1 className="mt-4 font-head text-3xl font-light leading-[1.08] tracking-tight text-[#16181D] md:text-5xl">{P.name} – {T("Granulat für Extrusion & Spritzguss", "granules for extrusion & injection moulding")}</h1>
              <p className="mt-6 max-w-xl text-lg leading-relaxed text-[#4a4d52]">{L(P.intro)}</p>
              <div className="mt-8 flex flex-wrap gap-3">
                <Link to="/kontakt" className="btn-primary" data-testid="product-request">{T("Muster anfragen", "Request sample")} <ArrowRight size={16} /></Link>
                <a href="#downloads" className="btn-ghost" data-testid="product-downloads-anchor">{T("Datenblätter", "Datasheets")}</a>
              </div>
            </Reveal>
          </div>
          <Reveal delay={0.1}>
            <img src={P.heroImage} alt={`${P.name} – NORDFLEX TPU PlastoNord`} className="aspect-[4/3] w-full rounded-2xl border border-[#e4e9f0] object-cover shadow-xl" loading="eager" width="900" height="675" />
          </Reveal>
        </div>
      </section>

      {/* PROPERTIES / ADVANTAGES / APPLICATIONS */}
      <Block title={T("Eigenschaften & Vorteile", "Properties & advantages")} icon={Sparkles}>
        <div className="grid gap-6 md:grid-cols-2">
          <div className="pn-card p-7">
            <h3 className="font-head text-lg font-semibold text-[#16181D]">{T("Eigenschaften", "Properties")}</h3>
            <ul className="mt-4 space-y-2.5">
              {P.properties.map((it, i) => <li key={i} className="flex items-start gap-2.5 text-[#4a4d52]"><Check size={17} className="mt-0.5 shrink-0 text-[#E2611C]" /> {L(it)}</li>)}
            </ul>
          </div>
          <div className="pn-card p-7">
            <h3 className="font-head text-lg font-semibold text-[#16181D]">{T("Vorteile bei PlastoNord", "Advantages at PlastoNord")}</h3>
            <ul className="mt-4 space-y-2.5">
              {P.advantages.map((it, i) => <li key={i} className="flex items-start gap-2.5 text-[#4a4d52]"><Check size={17} className="mt-0.5 shrink-0 text-[#E2611C]" /> {L(it)}</li>)}
            </ul>
            <h3 className="mt-6 font-head text-lg font-semibold text-[#16181D]">{T("Typische Anwendungen", "Typical applications")}</h3>
            <div className="mt-3 flex flex-wrap gap-2">
              {P.applications.map((a, i) => <span key={i} className="rounded-full bg-[#f0f2f5] px-3 py-1.5 text-sm text-[#4a4d52]">{L(a)}</span>)}
            </div>
          </div>
        </div>
      </Block>

      {/* GALLERY */}
      {P.gallery?.length > 0 && (
        <Block title={T("Produktansichten", "Product views")} icon={Boxes} id="galerie">
          <Stagger className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
            {P.gallery.map((g, i) => (
              <motion.figure key={i} variants={itemVariant} className="group overflow-hidden rounded-xl border border-[#e4e9f0] bg-white" data-testid={`gallery-${i}`}>
                <div className="overflow-hidden bg-[#f0f2f5]">
                  <img src={g.src} alt={g.alt} loading="lazy" className="aspect-[3/4] w-full object-contain transition-transform duration-500 group-hover:scale-[1.03]" />
                </div>
                <figcaption className="px-4 py-3 text-sm font-medium leading-snug text-[#16181D]">{L(g.cap)}</figcaption>
              </motion.figure>
            ))}
          </Stagger>
        </Block>
      )}

      {/* TYPES TABLE */}
      <section className="bg-[#f6f8fb]">
        <Block title={T("Typen & Härtebereiche", "Grades & hardness ranges")} icon={Boxes} id="typen">
          <ParamTable table={localTable(P.types)} testid="product-types-table" />
          <p className="mt-4 rounded-lg border-l-2 border-[#E2611C] bg-white px-4 py-3 text-sm text-[#4a4d52]"><strong className="text-[#16181D]">{T("Hinweis", "Note")}:</strong> {L(P.types.note)}</p>
          <p className="mt-4 text-sm text-[#4a4d52]">{T("Lagerbestände und Preise individuell – Verfügbarkeit auf Anfrage. Weitere Härten und Sondertypen auf Anfrage.", "Stock and prices are individual – availability on request. Further hardnesses and special grades on request.")}</p>
        </Block>
      </section>

      {/* TECHNICAL DATA */}
      <Block title={T("Technische Kennwerte", "Technical data")} icon={Sparkles} id="kennwerte">
        <ParamTable table={localTable(P.techData)} testid="product-techdata-table" />
        <p className="mt-4 rounded-lg border-l-2 border-[#E2611C] bg-[#f6f8fb] px-4 py-3 text-sm text-[#4a4d52]"><strong className="text-[#16181D]">{T("Hinweis", "Note")}:</strong> {L(P.techData.note)}</p>
      </Block>

      {/* PROCESSING PARAMETERS */}
      <Block title={T("Verarbeitungsparameter", "Processing parameters")} icon={Thermometer} id="verarbeitung">
        <div className="grid gap-6 lg:grid-cols-3">
          <div>
            <h3 className="mb-3 flex items-center gap-2 font-head text-lg font-semibold text-[#16181D]"><Droplets size={18} className="text-[#E2611C]" /> {T("Trocknung", "Drying")}</h3>
            <ParamTable table={localTable(P.processing.drying)} testid="product-drying-table" />
          </div>
          <div>
            <h3 className="mb-3 flex items-center gap-2 font-head text-lg font-semibold text-[#16181D]"><Factory size={18} className="text-[#E2611C]" /> {T("Spritzguss", "Injection moulding")}</h3>
            <ParamTable table={localTable(P.processing.injection)} testid="product-injection-table" />
          </div>
          <div>
            <h3 className="mb-3 flex items-center gap-2 font-head text-lg font-semibold text-[#16181D]"><Factory size={18} className="text-[#E2611C]" /> {T("Extrusion", "Extrusion")}</h3>
            <ParamTable table={localTable(P.processing.extrusion)} testid="product-extrusion-table" />
          </div>
        </div>
        <p className="mt-5 rounded-lg border-l-2 border-[#E2611C] bg-[#f6f8fb] px-4 py-3 text-sm text-[#4a4d52]"><strong className="text-[#16181D]">{T("Hinweis", "Note")}:</strong> {L(P.processing.note)}</p>
      </Block>

      {/* STORAGE */}
      <section className="bg-[#f6f8fb]">
        <Block title={T("Lagerung & Handhabung", "Storage & handling")} icon={Boxes}>
          <ul className="grid gap-2.5 md:grid-cols-2">
            {P.storage.map((it, i) => <li key={i} className="flex items-start gap-2.5 text-[#4a4d52]"><Check size={17} className="mt-0.5 shrink-0 text-[#E2611C]" /> {L(it)}</li>)}
          </ul>
        </Block>
      </section>

      {/* DOWNLOADS */}
      <Block title={T("Datenblätter & Werksprüfzeugnisse", "Datasheets & test certificates")} icon={FileText} id="downloads">
        {P.datasheets?.length > 0 && (
          <div className="mb-8">
            <h3 className="mb-3 font-head text-lg font-semibold text-[#16181D]">{T("Datenblätter & Werksprüfzeugnisse (auf Anfrage)", "Datasheets & works test certificates (on request)")}</h3>
            <div className="grid gap-4 sm:grid-cols-2">
              {P.datasheets.map((d, i) => (
                <Link key={i} to="/kontakt" className="pn-card flex items-center justify-between gap-3 p-5 transition-colors hover:border-[#E2611C]" data-testid={`datasheet-${i}`}>
                  <span className="flex items-center gap-3">
                    <FileText size={18} className="shrink-0 text-[#9aa0a8]" />
                    <span>
                      <span className="block font-medium text-[#16181D]">{d.type}</span>
                      <span className="block text-xs text-[#9aa0a8]">{L(d.base)}</span>
                    </span>
                  </span>
                  <span className="shrink-0 rounded-full bg-[#f0f2f5] px-3 py-1 text-xs font-medium text-[#4a4d52]">{T("auf Anfrage", "on request")}</span>
                </Link>
              ))}
            </div>
          </div>
        )}
        <h3 className="mb-3 font-head text-lg font-semibold text-[#16181D]">{T("Weitere Dokumente", "Further documents")}</h3>
        <div className="grid gap-4 sm:grid-cols-2">
          {P.downloads.map((d, i) => (
            d.file ? (
              <a key={i} href={d.file} target="_blank" rel="noopener noreferrer" download className="pn-card flex items-center justify-between p-5 transition-colors hover:border-[#E2611C]" data-testid={`download-${i}`}>
                <span className="flex items-center gap-3"><FileText size={18} className="text-[#E2611C]" /><span className="font-medium text-[#16181D]">{L(d.label)}</span></span>
                <span className="flex items-center gap-1 text-sm font-medium text-[#E2611C]">{d.type} <Download size={15} /></span>
              </a>
            ) : (
              <Link key={i} to="/kontakt" className="pn-card flex items-center justify-between p-5 transition-colors hover:border-[#E2611C]" data-testid={`download-${i}`}>
                <span className="flex items-center gap-3"><FileText size={18} className="text-[#9aa0a8]" /><span className="font-medium text-[#16181D]">{L(d.label)}</span></span>
                <span className="rounded-full bg-[#f0f2f5] px-3 py-1 text-xs font-medium text-[#4a4d52]">{T("auf Anfrage", "on request")}</span>
              </Link>
            )
          ))}
        </div>
        <p className="mt-4 text-sm text-[#4a4d52]">{T("Weitere Datenblätter und Prüfzeugnisse stellen wir laufend bereit. Für SDS und typspezifische Unterlagen kontaktieren Sie uns gern.", "We continuously provide further datasheets and test certificates. For SDS and grade-specific documents, please contact us.")}</p>
      </Block>

      {/* FAQ */}
      <section className="bg-[#f6f8fb]">
        <Block title={`${T("Häufige Fragen zu", "FAQ about")} ${P.name}`} icon={FileText} id="faq">
          <div className="max-w-3xl" data-testid="product-faq">
            <Accordion type="single" collapsible>
              {P.faq.map((f, i) => (
                <AccordionItem key={i} value={`q${i}`} data-testid={`product-faq-${i}`} className="border-b border-[#e4e9f0]">
                  <AccordionTrigger className="text-left font-head text-base font-medium text-[#16181D] hover:text-[#E2611C] hover:no-underline">{L(f.q)}</AccordionTrigger>
                  <AccordionContent className="leading-relaxed text-[#4a4d52]">{L(f.a)}</AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </div>
        </Block>
      </section>

      {/* INTERNAL LINKS */}
      <Block title={T("Verwandte Produkte & Wissen", "Related products & knowledge")} icon={ArrowUpRight}>
        <div className="grid gap-6 md:grid-cols-2">
          <div className="pn-card p-6">
            <p className="overline">{T("Verwandte Produktbereiche", "Related product areas")}</p>
            <ul className="mt-4 space-y-2">
              {P.relatedProducts.map((r, i) => (
                <li key={i}><Link to={r.href} className="flex items-center justify-between rounded-lg px-3 py-2.5 text-sm font-medium text-[#16181D] transition-colors hover:bg-[#f6f8fb] hover:text-[#E2611C]">{L(r.label)} <ArrowRight size={15} className="text-[#E2611C]" /></Link></li>
              ))}
            </ul>
          </div>
          <div className="pn-card p-6">
            <p className="overline">{T("Passende Wissensartikel", "Matching knowledge articles")}</p>
            <ul className="mt-4 space-y-2">
              {P.relatedKnowledge.map((r, i) => (
                <li key={i}><Link to={r.href} className="flex items-center justify-between rounded-lg px-3 py-2.5 text-sm font-medium text-[#16181D] transition-colors hover:bg-[#f6f8fb] hover:text-[#E2611C]">{L(r.label)} <ArrowUpRight size={15} className="text-[#E2611C]" /></Link></li>
              ))}
            </ul>
          </div>
        </div>
      </Block>

      {/* CTA */}
      <section className="pn-container pb-20">
        <div className="flex flex-wrap items-center gap-4 rounded-2xl bg-[#16181D] p-8">
          <div className="flex-1 min-w-[240px]">
            <h2 className="font-head text-xl font-semibold text-white">{T("Muster oder Angebot für", "Sample or quote for")} {P.name}?</h2>
            <p className="mt-2 text-sm leading-relaxed text-white/70">{T("Technische Beratung zu Typenauswahl, Härte und Verarbeitung.", "Technical advice on grade selection, hardness and processing.")}</p>
          </div>
          <Link to="/kontakt" className="btn-primary self-center" data-testid="product-cta">{T("Anfrage senden", "Send inquiry")} <ArrowRight size={16} /></Link>
        </div>
      </section>
    </div>
  );
}
